import { useEffect, useRef, useState, type ComponentProps, type RefObject } from "react";
import { cn } from "../lib/cn";
import { useUiLabels } from "../lib/ui-labels";

/**
 * Whether the wrapper is narrower than the table it holds — i.e. whether there is anything to scroll.
 *
 * <p>A subscription to the layout, so state set from the observer's callback, never from the
 * effect's body.
 */
function useOverflowsX(node: RefObject<HTMLDivElement | null>): boolean {
  const [overflows, setOverflows] = useState(false);
  useEffect(() => {
    const wrapper = node.current;
    if (!wrapper || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => {
      setOverflows(wrapper.scrollWidth - wrapper.clientWidth > 1);
    });
    observer.observe(wrapper);
    if (wrapper.firstElementChild) {
      observer.observe(wrapper.firstElementChild);
    }
    return () => observer.disconnect();
  }, [node]);
  return overflows;
}

/**
 * A table, in a wrapper that scrolls sideways when the table is wider than the screen.
 *
 * <p>A wrapper that scrolls has to be reachable from the keyboard, or the columns past its right
 * edge exist only for a mouse (WCAG 2.1.1; axe `scrollable-region-focusable`). It becomes a named,
 * focusable region **only while it actually scrolls**: a tab stop on every table that fits would be
 * one useless keypress per table for everybody who navigates with Tab, and a landmark per table
 * would bury the real ones.
 */
export function Table({ className, ...props }: ComponentProps<"table">) {
  const labels = useUiLabels();
  const wrapper = useRef<HTMLDivElement>(null);
  const scrolls = useOverflowsX(wrapper);
  return (
    <div
      ref={wrapper}
      className={cn(
        "relative w-full overflow-x-auto",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      )}
      {...(scrolls ? { tabIndex: 0, role: "region", "aria-label": labels.table } : {})}
    >
      <table
        className={cn(
          "w-full min-w-[42rem] caption-bottom text-sm",
          // Monospace in a table means an identifier — an order number, an SKU, a tracking code — and
          // an identifier does not wrap: "SO-2026-00255" split across three lines on a phone cannot be
          // read or copied (UX-15). The row scrolls sideways instead, which this wrapper is for.
          // Something mono that really is prose (a URL) says so with `whitespace-normal`.
          "[&_.font-mono]:whitespace-nowrap",
          // The same for a number with its unit: "170,4" on one line and "kg" on the next reads as two
          // values. Every amount, weight and percentage cell is `tabular-nums` already, so that is the
          // marker; a column of numbers is narrow by nature and the row scrolls if it has to.
          "[&_.tabular-nums]:whitespace-nowrap",
          className,
        )}
        {...props}
      />
    </div>
  );
}

export function TableHeader({ className, ...props }: ComponentProps<"thead">) {
  return <thead className={cn("[&_tr]:border-b [&_tr]:border-border", className)} {...props} />;
}

export function TableBody({ className, ...props }: ComponentProps<"tbody">) {
  return <tbody className={cn("[&_tr:last-child]:border-0", className)} {...props} />;
}

export function TableRow({ className, ...props }: ComponentProps<"tr">) {
  return (
    <tr className={cn("border-b border-border/70 transition-colors hover:bg-muted/60", className)} {...props} />
  );
}

export function TableHead({ className, ...props }: ComponentProps<"th">) {
  return (
    <th
      className={cn("h-11 px-4 text-left align-middle text-xs font-semibold uppercase tracking-wide text-muted-foreground", className)}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: ComponentProps<"td">) {
  return (
    <td
      // The vertical rhythm of a row is the application's decision, not this component's: a screen
      // full of amounts wants one density, a settings page another (EP-42 decision 2). The fallback
      // is what every application rendered before the variable existed, so nothing moves unless an
      // application sets it.
      className={cn("px-4 py-[var(--table-cell-py,0.75rem)] align-middle", className)}
      {...props}
    />
  );
}

export function TableCaption({ className, ...props }: ComponentProps<"caption">) {
  return <caption className={cn("mt-4 text-sm text-muted-foreground", className)} {...props} />;
}
